import React from 'react'
import { Layout, Button, Tooltip } from 'antd'
import { HomeOutlined, LogoutOutlined, SettingOutlined } from '@ant-design/icons'
import { authService } from '../../service/auth.service'
import { useNavigate } from 'react-router-dom'
import { useTheme, useThemeColors } from '../../ThemeContext'
import { useWelcomeSidebarSlotSafe } from '../../layout/WelcomeSidebarSlotContext'

const { Header } = Layout

function HeaderHomeSlot({ children }: { children: React.ReactNode }) {
	const slot = useWelcomeSidebarSlotSafe()?.slotWidthPx ?? 0
	return (
		<div
			style={{
				display: 'flex',
				alignItems: 'center',
				minWidth: 0,
				flex: '0 0 auto',
				width: 'max-content',
			}}
		>
			{slot > 0 ? <div style={{ width: slot, flexShrink: 0 }} aria-hidden /> : null}
			{children}
		</div>
	)
}

function GlobalHeader() {
	const navigate = useNavigate()
	const { theme, isDarkMode } = useTheme()
	const { primary } = useThemeColors()

	const isEDC = theme === 'edc'
	const isDark = theme === 'dark'

	const headerBg = isDark ? '#0f172a' : isEDC ? '#254a89' : '#414042'
	const headerBorder = isDark ? '1px solid #1e293b' : isEDC ? 'none' : '3px solid #ED1C24'
	const signOutBg = isEDC ? 'rgba(255,255,255,0.15)' : primary
	const signOutBorder = isEDC ? '1px solid rgba(255,255,255,0.4)' : primary

	const navToWelcomePage = () => {
		navigate('/welcome')
	}

	const handleLogout = () => {
		authService.logout()
		navigate('/login')
	}

	if (isDark) {
		return (
			<Header
				style={{
					position: 'sticky',
					top: 0,
					zIndex: 1000,
					width: '100%',
					backgroundColor: headerBg,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					padding: '0 24px',
					borderBottom: headerBorder,
					height: 64,
				}}
			>
				<HeaderHomeSlot>
					<Button
						type='text'
						icon={<HomeOutlined />}
						onClick={navToWelcomePage}
						style={{ color: '#f8fafc', fontWeight: 500 }}
					>
						Home
					</Button>
				</HeaderHomeSlot>
				<div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
					<Tooltip title='Settings'>
						<Button
							type='text'
							icon={<SettingOutlined />}
							onClick={() => navigate('/settings')}
							style={{ color: '#f8fafc', fontSize: 18 }}
						/>
					</Tooltip>
					<Button
						icon={<LogoutOutlined />}
						onClick={handleLogout}
						style={{
							backgroundColor: 'rgba(56, 189, 248, 0.08)',
							borderColor: primary,
							color: primary,
							fontWeight: 600,
							borderRadius: 8,
						}}
					>
						Sign Out
					</Button>
				</div>
			</Header>
		)
	}

	if (isEDC) {

		// EDC: Home left, actions right
		return (
			<Header
				style={{
					position: 'sticky',
					top: 0,
					zIndex: 1000,
					width: '100%',
					backgroundColor: '#254a89',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					padding: '0 20px',
				}}
			>
				{/* Left: Home */}
				<HeaderHomeSlot>
					<Button type='default' icon={<HomeOutlined />} onClick={navToWelcomePage}>
						Home
					</Button>
				</HeaderHomeSlot>

				{/* Right: Sign Out + Settings */}
				<div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
					<Button type='default' icon={<LogoutOutlined />} onClick={handleLogout}>
						Sign Out
					</Button>
					<Tooltip title='Settings'>
						<SettingOutlined
							style={{
								fontSize: '20px',
								cursor: 'pointer',
								color: 'white',
							}}
							onClick={() => navigate('/settings')}
						/>
					</Tooltip>
				</div>
			</Header>
		)
	}

	// Primersoft: modern charcoal + red border header
	return (
		<Header
			style={{
				position: 'sticky',
				top: 0,
				zIndex: 1000,
				width: '100%',
				backgroundColor: headerBg,
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'space-between',
				padding: '0 24px',
				borderBottom: headerBorder,
				height: 56,
				lineHeight: '56px',
			}}
		>
			{/* Left: Home */}
			<HeaderHomeSlot>
				<Button
					type='text'
					icon={<HomeOutlined />}
					onClick={navToWelcomePage}
					style={{ color: '#ffffff', fontWeight: 500, fontSize: 14 }}
				>
					Home
				</Button>
			</HeaderHomeSlot>

			{/* Right: Settings + Sign Out */}
			<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
				<Tooltip title='Settings'>
					<Button
						type='text'
						icon={<SettingOutlined />}
						onClick={() => navigate('/settings')}
						style={{ color: 'rgba(255,255,255,0.85)', fontSize: 14 }}
					/>
				</Tooltip>
				<Button
					icon={<LogoutOutlined />}
					onClick={handleLogout}
					style={{
						backgroundColor: signOutBg,
						borderColor: signOutBorder,
						color: '#ffffff',
						fontWeight: 500,
						fontSize: 14,
					}}
				>
					Sign Out
				</Button>
			</div>
		</Header>
	)
}

export default GlobalHeader
